/**
 * community-intelligence/models.ts — types for Engine #7, the Roamer Community
 * Intelligence Engine™.
 *
 * A trusted community knowledge system (NOT a social platform): it collects,
 * verifies, aggregates, and distributes knowledge contributed by remote
 * workers. Verified contributions and high-reputation contributors carry more
 * weight; every aggregated recommendation explains itself; participation is
 * strictly opt-in.
 */

// ─── Contributions ─────────────────────────────────────────────────────────────

/** Everything the community can contribute (spec list). */
export type ContributionType =
  | 'cafeReview'
  | 'coworkingReview'
  | 'apartmentReview'
  | 'neighborhoodReview'
  | 'internetSpeedReport'
  | 'noiseLevelReport'
  | 'powerReliabilityReport'
  | 'safetyReport'
  | 'transitTip'
  | 'localAdvice'
  | 'scamWarning'
  | 'visaExperience'
  | 'healthcareExperience'
  | 'costReport'
  | 'favoriteLocation'
  | 'hiddenGem'
  | 'photographySpot'
  | 'weekendActivity'
  | 'monthlyBudgetReport';

/** Provenance tiers — verified members carry the most weight. */
export type ContributorTier = 'verified' | 'anonymous' | 'external';

export interface CommunityContribution {
  id: string;
  type: ContributionType;
  /** Place / neighborhood / city the knowledge is about. */
  targetId: string;
  targetLabel: string;
  locationId: string;
  contributorId: string;
  tier: ContributorTier;
  /** 1–5 when the contribution is a review/report with a rating. */
  rating: number | null;
  /** Structured facts (internetMbps: 310, noiseLevel: 'quiet', timeWindow: 'before-10am', …). */
  attributes: Record<string, string | number | boolean>;
  /** Free-text tip/advice (optional). */
  text: string | null;
  helpfulVotes: number;
  inaccurateReports: number;
  /** Private contributions are stored but excluded from aggregation. */
  visibility: 'public' | 'private';
  at: number;
}

// ─── Contributors & reputation ─────────────────────────────────────────────────

/** Raw reputation signals (spec list) — the score is derived, never stored. */
export interface ReputationSignals {
  helpfulVotes: number;
  verifiedCheckIns: number;
  missionsCompleted: number;
  /** Distinct cities with contributions/check-ins. */
  travelExperience: number;
  /** 0–1: share of contributions with structured attributes or text. */
  contributionQuality: number;
  /** 0–1: community trust (endorsements ratio). */
  communityTrust: number;
  /** 0–1: 1 - inaccurate/(helpful+inaccurate). */
  reportAccuracy: number;
}

export interface ContributorProfile {
  id: string;
  tier: ContributorTier;
  displayName: string;
  signals: ReputationSignals;
  /** Derived 0–100. Higher reputation ⇒ higher recommendation weight. */
  reputation: number;
  joinedAt: number;
}

// ─── Aggregated intelligence (engine output) ───────────────────────────────────

/** Community metric tags (spec list). */
export type CommunitySignalTag =
  | 'mostRecommended'
  | 'trending'
  | 'hiddenGem'
  | 'recentlyImproved'
  | 'recentlyDeclined'
  | 'highlyTrusted'
  | 'newDiscovery'
  | 'communityFavorite';

export interface CommunityExplanation {
  summary: string;
  /** Plain-English evidence ("42 verified WorkRoamers recommend this location."). */
  reasons: string[];
}

/** Aggregated knowledge about one target, ready for consuming engines. */
export interface TargetIntelligence {
  targetId: string;
  targetLabel: string;
  locationId: string;
  /** Weighted 1–5 community rating (verified + reputation weighted). */
  communityRating: number | null;
  /** Total contribution count feeding this aggregate (public only). */
  contributionCount: number;
  verifiedCount: number;
  /** Weighted share of evidence from verified members, 0–1. */
  verifiedWeightShare: number;
  /** Aggregated structured facts (avg internetMbps, dominant noiseLevel, …). */
  facts: Record<string, string | number>;
  signals: CommunitySignalTag[];
  /** Never omitted — every community recommendation explains why. */
  explanation: CommunityExplanation;
  updatedAt: number;
}

/** A time/context-scoped local insight ("Best café before 10 AM"). */
export interface LocalInsight {
  locationId: string;
  topic: string;
  targetId: string;
  targetLabel: string;
  score: number;
  explanation: CommunityExplanation;
}

// ─── Privacy ───────────────────────────────────────────────────────────────────

export interface CommunityPrivacySettings {
  /** Master switch — participation is ALWAYS opt-in (default false). */
  participationEnabled: boolean;
  publicProfile: boolean;
  /** When true, new contributions are recorded as tier 'anonymous'. */
  anonymousContributions: boolean;
  /** When false, contributions carry no location attributes beyond the city. */
  locationSharing: boolean;
  /** Default visibility for new reviews. */
  reviewVisibility: 'public' | 'private';
}

export const DEFAULT_PRIVACY: CommunityPrivacySettings = {
  participationEnabled: false, // opt-in, never opt-out
  publicProfile: false,
  anonymousContributions: false,
  locationSharing: true,
  reviewVisibility: 'public',
};
